import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import {
  approveRequisition,
  cancelRequisition,
  createRequisition,
  listInterviews,
  listRequisitions,
  rejectRequisition,
  submitInterviewFeedback,
} from "../../api/recruitment";
import {
  canActOnRequisition,
  isOwnRequisition,
  isOpenRequisition,
  requisitionCreatorName,
  requisitionNextApproverLabel,
  requisitionStatusLabel,
} from "../../utils/recruitmentRequisition";
import "../hr/HrRecruitment.css";

function apiError(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function unwrap(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

export default function ManagerRecruitment() {
  const { user } = useAuth();
  const { departments } = useData();
  const [error, setError] = useState("");
  const [requisitions, setRequisitions] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [form, setForm] = useState({
    title: "",
    departmentId: user?.departmentBackendId || "",
    reason: "",
    headcount: 1,
  });

  const load = useCallback(async () => {
    setError("");
    try {
      const [req, ints] = await Promise.all([
        listRequisitions(),
        listInterviews(),
      ]);
      setRequisitions(unwrap(req));
      setInterviews(unwrap(ints));
    } catch (err) {
      setError(apiError(err, "تعذر تحميل بيانات التوظيف"));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const deptOptions = useMemo(
    () =>
      (departments || [])
        .filter((d) => d.isActive !== false && (d.backendId || d.id))
        .map((d) => ({ id: d.backendId || d.id, name: d.name })),
    [departments],
  );

  return (
    <div className="hr-recruitment">
      <div className="hr-recruitment__header">
        <div>
          <h1 className="hr-recruitment__title">التوظيف</h1>
          <p className="hr-recruitment__subtitle">طلب شاغر وتقييم المقابلات المطلوبة منك</p>
        </div>
      </div>
      {error ? <div className="hr-recruitment__error">{error}</div> : null}

      <div className="hr-recruitment__panel">
        <h2 className="hr-recruitment__title" style={{ fontSize: "1rem" }}>طلب شاغر جديد</h2>
        <form
          className="hr-recruitment__form"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              await createRequisition({
                title: form.title,
                departmentId: form.departmentId,
                branchId: user?.branchId || undefined,
                reason: form.reason,
                headcount: Number(form.headcount) || 1,
              });
              setForm({
                title: "",
                departmentId: user?.departmentBackendId || "",
                reason: "",
                headcount: 1,
              });
              await load();
            } catch (err) {
              setError(apiError(err, "تعذر تقديم الطلب"));
            }
          }}
        >
          <input placeholder="المسمى الوظيفي" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          <select value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })} required>
            <option value="">الإدارة</option>
            {deptOptions.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
          <input type="number" min="1" value={form.headcount} onChange={(e) => setForm({ ...form, headcount: e.target.value })} />
          <textarea placeholder="سبب الطلب" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} required />
          <div className="hr-recruitment__actions">
            <button className="hr-recruitment__btn" type="submit">تقديم</button>
          </div>
        </form>
      </div>

      <div className="hr-recruitment__panel">
        <h2 className="hr-recruitment__title" style={{ fontSize: "1rem" }}>طلبات الشواغر</h2>
        <table className="hr-recruitment__table">
          <thead>
            <tr>
              <th>المسمى</th>
              <th>مقدم الطلب</th>
              <th>الموافق التالي</th>
              <th>الحالة</th>
              <th>إجراء</th>
            </tr>
          </thead>
          <tbody>
            {requisitions.map((row) => (
              <tr key={row.id}>
                <td>{row.title}</td>
                <td>{requisitionCreatorName(row)}</td>
                <td>{requisitionNextApproverLabel(row)}</td>
                <td>{requisitionStatusLabel(row.status || row.request?.status)}</td>
                <td>
                  {isOpenRequisition(row) && (
                    <div className="hr-recruitment__actions">
                      {isOwnRequisition(row, user) ? (
                        <button type="button" className="hr-recruitment__btn hr-recruitment__btn--ghost" onClick={async () => {
                          if (!window.confirm("هل تريد إلغاء طلب الشاغر؟ يمكنك تقديم طلب جديد بعد الإلغاء.")) return;
                          try { await cancelRequisition(row.id); await load(); } catch (err) { setError(apiError(err, "تعذر إلغاء الطلب")); }
                        }}>إلغاء الطلب</button>
                      ) : canActOnRequisition(row, user) ? (
                        <>
                          <button type="button" className="hr-recruitment__btn" onClick={async () => {
                            try { await approveRequisition(row.id, {}); await load(); } catch (err) { setError(apiError(err, "تعذر الاعتماد")); }
                          }}>موافقة</button>
                          <button type="button" className="hr-recruitment__btn hr-recruitment__btn--danger" onClick={async () => {
                            try { await rejectRequisition(row.id, { notes: "مرفوض" }); await load(); } catch (err) { setError(apiError(err, "تعذر الرفض")); }
                          }}>رفض</button>
                        </>
                      ) : null}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="hr-recruitment__panel">
        <h2 className="hr-recruitment__title" style={{ fontSize: "1rem" }}>مقابلاتي</h2>
        <table className="hr-recruitment__table">
          <thead>
            <tr><th>المتقدم</th><th>الموعد</th><th>تقييم</th></tr>
          </thead>
          <tbody>
            {interviews.map((row) => (
              <tr key={row.id}>
                <td>{row.application?.candidate?.fullName}</td>
                <td>{row.scheduledAt ? new Date(row.scheduledAt).toLocaleString("ar-EG") : "—"}</td>
                <td>
                  <button type="button" className="hr-recruitment__btn hr-recruitment__btn--ghost" onClick={async () => {
                    const score = window.prompt("الدرجة من 100", "80");
                    if (score == null) return;
                    try {
                      await submitInterviewFeedback(row.id, {
                        score: Number(score),
                        status: "COMPLETED",
                        outcome: "PASS",
                      });
                      await load();
                    } catch (err) {
                      setError(apiError(err, "تعذر حفظ التقييم"));
                    }
                  }}>تقييم</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
